import { computed, observable, type IComputedValue, type IObservableValue } from 'mobx';

// Dropdown page size: how many rows are visible before "load more", and
// how many each "load more" reveals.
export const PAGE_SIZE = 10;
export const PAGE_STEP = 10;
// Default size of a `search` source's initial fetch. `total <= threshold`
// collapses the control to client-side filtering (hold the universe, no
// further requests); above it, the control stays server-side.
export const DEFAULT_THRESHOLD = 1000;

const matchesQuery = (label: string, query: string): boolean => label.toLowerCase().includes(query.toLowerCase());

/**
 * One mode of an EditReference's option list. The model swaps between
 * implementations as loading resolves (State pattern), so neither the
 * model nor the component branches on mode — each state encapsulates its
 * own `onSearch` / `loadMore` and the derived counts the footer reads.
 *
 * `options` is already sliced to the current page; "showing
 * `shownCount` of `filteredCount`" describes the footer, and
 * `canLoadMore` whether another page exists. `reset` returns to the
 * initial (empty-query) set; `findByKey` looks a value up in whatever the
 * state currently holds, so the model can hydrate a selection's label.
 */
export interface ReferenceState<T> {
  readonly options: readonly T[];
  readonly isLoading: boolean;
  readonly shownCount: number;
  readonly filteredCount: number;
  readonly canLoadMore: boolean;
  readonly emptyLabel: string;
  onSearch(query: string): void;
  loadMore(): void;
  reset(): void;
  findByKey(key: string | number): T | null;
}

/**
 * Pre-resolution state for `eager` / `search` sources. Holds nothing but
 * a buffer of the latest query the user typed while loading, so the
 * model can replay it once the real state takes over.
 */
export class LoadingState<T> implements ReferenceState<T> {
  private readonly _buffered: IObservableValue<string> = observable.box('');

  get options(): readonly T[] {
    return [];
  }
  get isLoading(): boolean {
    return true;
  }
  get shownCount(): number {
    return 0;
  }
  get filteredCount(): number {
    return 0;
  }
  get canLoadMore(): boolean {
    return false;
  }
  get emptyLabel(): string {
    return 'Loading…';
  }
  get bufferedQuery(): string {
    return this._buffered.get();
  }

  onSearch = (query: string): void => {
    this._buffered.set(query);
  };
  loadMore = (): void => {};
  reset = (): void => {
    this._buffered.set('');
  };
  findByKey = (_key: string | number): T | null => null;
}

/** Terminal state when the initial load rejected. */
export class ErrorState<T> implements ReferenceState<T> {
  constructor(private readonly _error: Error) {}

  get options(): readonly T[] {
    return [];
  }
  get isLoading(): boolean {
    return false;
  }
  get shownCount(): number {
    return 0;
  }
  get filteredCount(): number {
    return 0;
  }
  get canLoadMore(): boolean {
    return false;
  }
  get emptyLabel(): string {
    return `Error: ${this._error.message}`;
  }

  onSearch = (): void => {};
  loadMore = (): void => {};
  reset = (): void => {};
  findByKey = (_key: string | number): T | null => null;
}

/**
 * Whole universe held in memory; `onSearch` filters it case-insensitively
 * on the label and `loadMore` just reveals more rows. No network, ever —
 * used by `static`, `eager`, and a `search` source that collapsed to
 * client-side because its total fit under the threshold. Filtering is
 * synchronous, so there's no in-flight state to flicker through.
 */
export class ClientState<T> implements ReferenceState<T> {
  private readonly _query: IObservableValue<string> = observable.box('');
  private readonly _limit: IObservableValue<number> = observable.box(PAGE_SIZE);
  private readonly _filtered: IComputedValue<readonly T[]>;

  constructor(
    private readonly _universe: readonly T[],
    private readonly _getKey: (item: T) => string | number,
    private readonly _getLabel: (item: T) => string
  ) {
    this._filtered = computed(() => {
      const q = this._query.get();
      if (q === '') return this._universe;
      return this._universe.filter((item) => matchesQuery(this._getLabel(item), q));
    });
  }

  get options(): readonly T[] {
    return this._filtered.get().slice(0, this._limit.get());
  }
  get isLoading(): boolean {
    return false;
  }
  get filteredCount(): number {
    return this._filtered.get().length;
  }
  get shownCount(): number {
    return Math.min(this._limit.get(), this.filteredCount);
  }
  get canLoadMore(): boolean {
    return this.shownCount < this.filteredCount;
  }
  get emptyLabel(): string {
    return 'No matches';
  }

  onSearch = (query: string): void => {
    this._query.set(query);
    this._limit.set(PAGE_SIZE);
  };
  loadMore = (): void => {
    this._limit.set(this._limit.get() + PAGE_STEP);
  };
  reset = (): void => {
    this._query.set('');
    this._limit.set(PAGE_SIZE);
  };
  findByKey = (key: string | number): T | null => this._universe.find((item) => this._getKey(item) === key) ?? null;
}

/**
 * Server-side mode for a universe larger than the threshold. Holds the
 * page returned for the current query.
 *
 * A typed query is a *new* search: the held rows are cleared so the menu
 * shows "Searching…" rather than stale results, then replaced when the
 * response lands. `loadMore` is different — it keeps the current rows on
 * screen, slicing them client-side until it runs past them, only then
 * fetching a larger page. `reset` restores the initial empty-query page
 * without a round-trip.
 */
export class ServerState<T> implements ReferenceState<T> {
  private readonly _items: IObservableValue<readonly T[]>;
  private readonly _total: IObservableValue<number>;
  private readonly _query: IObservableValue<string> = observable.box('');
  private readonly _limit: IObservableValue<number> = observable.box(PAGE_SIZE);
  private readonly _pending: IObservableValue<boolean> = observable.box(false);
  private readonly _initialItems: readonly T[];
  private readonly _initialTotal: number;
  // Size of the last server page requested; load-more grows it by a
  // further `threshold` when it needs rows beyond what's held.
  private _fetchedLimit: number;
  // Monotonic guard so a slow earlier response (or one a reset/new-query
  // superseded) can't overwrite a newer one.
  private _seq = 0;

  constructor(
    initialItems: readonly T[],
    initialTotal: number,
    private readonly _search: (query: string, limit: number) => Promise<{ readonly items: readonly T[]; readonly total: number }>,
    private readonly _threshold: number,
    private readonly _getKey: (item: T) => string | number
  ) {
    this._items = observable.box(initialItems, { deep: false });
    this._total = observable.box(initialTotal);
    this._initialItems = initialItems;
    this._initialTotal = initialTotal;
    this._fetchedLimit = _threshold;
  }

  get options(): readonly T[] {
    return this._items.get().slice(0, this._limit.get());
  }
  get isLoading(): boolean {
    return this._pending.get();
  }
  get filteredCount(): number {
    return this._total.get();
  }
  get shownCount(): number {
    return Math.min(this._limit.get(), this._items.get().length);
  }
  get canLoadMore(): boolean {
    return this.shownCount < this._total.get();
  }
  get emptyLabel(): string {
    return this._pending.get() ? 'Searching…' : 'No matches';
  }

  onSearch = (query: string): void => {
    if (query === this._query.get()) return;
    this._query.set(query);
    this._limit.set(PAGE_SIZE);
    // New query → clear the held rows so the menu shows "Searching…"
    // instead of the previous query's (stale) results.
    this._items.set([]);
    this._total.set(0);
    this._fetch(query, this._threshold);
  };

  loadMore = (): void => {
    const next = this._limit.get() + PAGE_STEP;
    const held = this._items.get().length;
    this._limit.set(next);
    // Past what we hold but the server has more — grow the page. The rows
    // already on screen stay (no clear) while the larger page loads.
    if (next > held && held < this._total.get()) {
      this._fetch(this._query.get(), this._fetchedLimit + this._threshold);
    }
  };

  reset = (): void => {
    this._seq++; // invalidate any in-flight fetch
    this._query.set('');
    this._limit.set(PAGE_SIZE);
    this._pending.set(false);
    this._items.set(this._initialItems);
    this._total.set(this._initialTotal);
    this._fetchedLimit = this._threshold;
  };

  // Look in the current page first, then the (larger) initial page — a
  // value selected before a search narrowed the rows is still found there
  // without a `resolve` round-trip.
  findByKey = (key: string | number): T | null =>
    this._items.get().find((item) => this._getKey(item) === key) ?? this._initialItems.find((item) => this._getKey(item) === key) ?? null;

  private _fetch = (query: string, limit: number): void => {
    const seq = ++this._seq;
    this._pending.set(true);
    this._search(query, limit).then(
      (res) => {
        if (seq !== this._seq) return;
        this._items.set(res.items);
        this._total.set(res.total);
        this._fetchedLimit = limit;
        this._pending.set(false);
      },
      () => {
        if (seq !== this._seq) return;
        this._pending.set(false);
      }
    );
  };
}
